import {
  JsonMetadata,
  Metadata,
  Metaplex,
  Nft,
  NftWithToken,
  WithdrawFromBuyerAccountBuilderParams,
  TransactionBuilderOptions,
  TransactionBuilder,
  WithdrawFromBuyerAccountBuilderContext,
  AuctioneerAuthorityRequiredError,
  isSigner,
  WithdrawFromBuyerAccountRequiresSignerError,
  Signer,
  toPublicKey,
} from '@metaplex-foundation/js'
import {
  createAuctioneerWithdrawInstruction,
  createWithdrawInstruction,
  WithdrawInstructionAccounts,
} from '@metaplex-foundation/mpl-auction-house'
import {
  Cluster,
  clusterApiUrl,
  Connection,
  PUBLIC_KEY_LENGTH,
  PublicKey,
} from '@solana/web3.js'
import { searchAssets } from '@helium/spl-utils'
import { AnchorProvider } from '@coral-xyz/anchor'
import { CompressedNFT } from 'types/solana'
import { WrappedConnection } from './WrappedConnection'
import {
  getPendingRewardsForHotspots,
  heliumEntityCreator,
} from './treasuryHotspots'

const DEWICAT_COLLECTION = process.env.NEXT_PUBLIC_COLLECTION_MINT

export const SolanaConnection = {
  localnet: new WrappedConnection('http://127.0.0.1:8899'),
  devnet: new WrappedConnection(
    'https://rpc-devnet.helius.xyz/?api-key=4eb6e438-25d1-4dcf-89dd-f54f2b42b846',
  ),
  devnetNonAssetApi: new Connection(clusterApiUrl('devnet')),
  testnet: new WrappedConnection(clusterApiUrl('testnet')),
  testnetNonAssetApi: new Connection(clusterApiUrl('testnet')),
  'mainnet-beta': new WrappedConnection(
    process.env.NEXT_PUBLIC_REACT_APP_SOLANA_RPC_HOST || '',
  ),
} as const

export const getConnection = (cluster: Cluster) =>
  SolanaConnection[cluster] || SolanaConnection.devnet

export const getHeliusAsset = async (
  anchorProvider: AnchorProvider,
  mint: PublicKey,
) => {
  const response = await fetch(anchorProvider.connection.rpcEndpoint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      jsonrpc: '2.0',
      id: 'my-id',
      method: 'getAsset',
      params: {
        id: mint.toBase58(),
      },
    }),
  })
  const { result } = await response.json()

  return result
}

export const getSignaturesForAsset = async (
  anchorProvider: AnchorProvider,
  mint: PublicKey,
) => {
  const response = await fetch(anchorProvider.connection.rpcEndpoint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      jsonrpc: '2.0',
      id: 'dewicats-web',
      method: 'getSignaturesForAsset',
      params: {
        id: mint.toBase58(),
        page: 1, // Starts at 1
        limit: 1000, // Limit 1000 per request.
      },
    }),
  })
  const { result } = await response.json()

  return result
}

export const getCollectables = async (
  pubKey: PublicKey,
  metaplex: Metaplex,
): Promise<Collectable[]> => {
  const collectables = (await metaplex
    .nfts()
    .findAllByOwner({ owner: pubKey })) as Metadata<JsonMetadata<string>>[]

  const collectablesWithMetadata = await Promise.all(
    collectables.map((col) => metaplex.nfts().load({ metadata: col })),
  )

  return collectablesWithMetadata.filter(
    (col) => col.model === 'nft',
  ) as Collectable[]
}

type EllipsizeOpts = {
  numChars?: number
}

export const ellipsizeAddress = (address: string, options?: EllipsizeOpts) => {
  if (!address) return ''
  const numChars = options?.numChars || 8
  return [address.slice(0, numChars), address.slice(-numChars)].join('...')
}

export const getCompressedCollectablesByCreator = async (
  provider: AnchorProvider,
  cluster: Cluster,
  page?: number,
  limit?: number,
) => {
  const conn = getConnection(cluster)
  // @ts-ignore
  const items = await searchAssets(conn.rpcEndpoint, {
    ownerAddress: provider.publicKey.toBase58(),
    creatorVerified: true,
    creatorAddress: heliumEntityCreator().toBase58(),
    page,
    limit,
    options: {
      showCollectionMetadata: true,
    },
  })

  const hotspotsWithPendingRewards = await annotateWithPendingRewards(
    provider,
    items as unknown as CompressedNFT[],
  )

  return hotspotsWithPendingRewards
}

export type Collectable = Nft | NftWithToken

export type HotspotWithPendingRewards = CompressedNFT & {
  // mint id to pending rewards
  pendingRewards: Record<string, string> | undefined
}

export async function annotateWithPendingRewards(
  provider: AnchorProvider,
  hotspots: CompressedNFT[],
): Promise<HotspotWithPendingRewards[]> {
  try {
    const rewards = await getPendingRewardsForHotspots(provider, hotspots)

    return hotspots.map((hotspot, index) => {
      const hotspotWithMeta: HotspotWithPendingRewards =
        hotspot as HotspotWithPendingRewards
      hotspotWithMeta.pendingRewards = rewards[index]
      return hotspotWithMeta
    })
  } catch (e) {
    console.error('Failed to get pending rewards', e)
    return hotspots as HotspotWithPendingRewards[]
  }
}

export const clusterByUrl = (url?: string): Cluster => {
  if (!url) return 'mainnet-beta'
  if (url.includes('mainnet') || url.includes('https://rpc.helius.xyz'))
    return 'mainnet-beta'
  if (url.includes('testnet')) return 'testnet'
  if (url.includes('devnet')) return 'devnet'
  return 'mainnet-beta'
}

export const getDeWiCatCollectables = async (
  anchorProvider: AnchorProvider,
  page?: number,
  limit?: number,
) => {
  const conn = anchorProvider.connection
  // @ts-ignore
  const items = await searchAssets(conn.rpcEndpoint, {
    ownerAddress: anchorProvider.publicKey.toBase58(),
    creatorVerified: true,
    grouping: ['collection', DEWICAT_COLLECTION],
    page,
    limit,
  })

  if (!items) {
    return []
  }

  return (items as unknown as (CompressedNFT & { burnt: boolean })[]).filter(
    (n) => !n.burnt && !n.content.json_uri.includes('box'),
  )
}

export const solAddressIsValid = (address: string) => {
  try {
    const pubKey = new PublicKey(address)
    return pubKey.toBuffer().length === PUBLIC_KEY_LENGTH
  } catch {
    return false
  }
}

export const withdrawFromBuyerAccountBuilder = (
  metaplex: Metaplex,
  params: WithdrawFromBuyerAccountBuilderParams,
  options: TransactionBuilderOptions = {},
): TransactionBuilder<WithdrawFromBuyerAccountBuilderContext> => {
  const { programs, payer = metaplex.rpc().getDefaultFeePayer() } = options
  const { auctionHouse, auctioneerAuthority, amount } = params

  if (auctionHouse.hasAuctioneer && !params.auctioneerAuthority) {
    throw new AuctioneerAuthorityRequiredError()
  }

  const amountBasisPoint = amount.basisPoints
  const buyer = params.buyer ?? (metaplex.identity() as Signer)
  const authority = params.authority ?? auctionHouse.authorityAddress

  if (!isSigner(buyer) && !isSigner(authority)) {
    throw new WithdrawFromBuyerAccountRequiresSignerError()
  }

  const escrowPayment = metaplex
    .auctionHouse()
    .pdas()
    .buyerEscrow({
      auctionHouse: auctionHouse.address,
      buyer: toPublicKey(buyer),
      programs,
    })

  // Accounts,
  const accounts: WithdrawInstructionAccounts = {
    wallet: toPublicKey(buyer),
    receiptAccount: metaplex
      .tokens()
      .pdas()
      .associatedTokenAccount({
        mint: auctionHouse.treasuryMint.address,
        owner: toPublicKey(buyer),
        programs,
      }),
    escrowPaymentAccount: escrowPayment,
    treasuryMint: auctionHouse.treasuryMint.address,
    authority: toPublicKey(authority),
    auctionHouse: auctionHouse.address,
    auctionHouseFeeAccount: auctionHouse.feeAccountAddress,
  }

  // Args.
  const args = {
    escrowPaymentBump: escrowPayment.bump,
    amount: amountBasisPoint,
  }

  // Withdraw Instruction.
  let withdrawInstruction = createWithdrawInstruction(accounts, args)
  if (auctioneerAuthority) {
    const ahAuctioneerPda = metaplex.auctionHouse().pdas().auctioneer({
      auctionHouse: auctionHouse.address,
      auctioneerAuthority: auctioneerAuthority.publicKey,
      programs,
    })

    const accountsWithAuctioneer = {
      ...accounts,
      auctioneerAuthority: auctioneerAuthority.publicKey,
      ahAuctioneerPda,
    }

    withdrawInstruction = createAuctioneerWithdrawInstruction(
      accountsWithAuctioneer,
      args,
    )
  }

  // Signers.
  const signer = isSigner(buyer) ? buyer : (authority as Signer)
  const withdrawSigners = [signer, params.auctioneerAuthority].filter(isSigner)

  // Update the account to be a signer since it's not covered properly by MPL due to its dynamic nature.
  const signerKeyIndex = withdrawInstruction.keys.findIndex((key) =>
    key.pubkey.equals(signer.publicKey),
  )
  withdrawInstruction.keys[signerKeyIndex].isSigner = true

  return (
    TransactionBuilder.make()
      .setFeePayer(payer)

      // Withdraw.
      .add({
        instruction: withdrawInstruction,
        signers: withdrawSigners,
        key: params.instructionKey ?? 'withdrawFromBuyerAccount',
      })
  )
}

export const getAssetsByGroup = async ({
  pageNumber,
  pageSize,
}: {
  pageNumber: number
  pageSize: number
}) => {
  const response = await fetch(
    process.env.NEXT_PUBLIC_REACT_APP_SOLANA_RPC_HOST,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        jsonrpc: '2.0',
        id: 'my-id',
        method: 'getAssetsByGroup',
        params: {
          groupKey: 'collection',
          groupValue: process.env.NEXT_PUBLIC_COLLECTION_MINT,
          page: pageNumber, // Starts at 1
          limit: pageSize,
        },
      }),
    },
  )

  const { result } = await response.json()
  return result
}

export const MININUM_BID_INCREMENT: Record<string, number> = {
  MOBILE: 10_000,
  HNT: 1,
  IOT: 10_000,
  HONEY: 500,
  TOKE: 25_000,
  RENDER: 5,
  hSOL: 0.2,
  DEFAULT: 0.0001,
  JUP: 1,
  UPDOG: 10_000,
  MPLX: 100,
  XNET: 300,
  ENT: 100_000,
  META: 0.0001,
  SOL: 0.000000001,
  FOUNDERS: 1,
}
